import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useSettings } from "../../context/SettingsContext";
import { useUsdTry, displayPrice } from "../../lib/price";

const FALLBACK = [
  "https://images.unsplash.com/photo-1545292470-391a7b77b8a2?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000",
  "https://images.unsplash.com/photo-1694974249671-2a921a644a1c?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000",
  "https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000",
];

export const BannerSlider = ({ projects = [], ready = true }) => {
  const { t, tc, dir, lang } = useLang();
  const { settings } = useSettings();
  const rate = useUsdTry();
  const slides = projects.length
    ? projects.map((p) => ({
        image: p.image,
        imageVariants: p.image_variants || {},
        title: tc(p.title),
        location: p.location,
        price: displayPrice(p.price, p.price_try, lang, rate),
        slug: p.slug,
        type: tc(p.project_type),
      }))
    : ready
    ? FALLBACK.map((image) => ({ image, title: t.hero.line1, location: "", price: "", slug: null, type: "" }))
    : [];

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  const go = useCallback((n) => setIdx((prev) => (n + count) % count), [count]);

  // Warm up the next slide so transitions never wait on the network.
  useEffect(() => {
    if (count <= 1) return;
    const next = slides[(idx + 1) % count];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const url = (isMobile ? next?.imageVariants?.mobile?.url : next?.imageVariants?.desktop?.url) || next?.image;
    if (!url) return;
    const img = new Image();
    img.src = url;
  }, [idx, count, slides]);

  useEffect(() => {
    if (paused || count <= 1) return;
    const timer = setInterval(() => setIdx((p) => (p + 1) % count), 5500);
    return () => clearInterval(timer);
  }, [paused, count]);

  const slide = slides[idx];
  const isRTL = dir === "rtl";
  const imageOpacity = Math.min(100, Math.max(20, Number(settings.banner_image_opacity ?? 100))) / 100;
  // Higher opacity also lifts the dark veil so the photo reads clearly at 100%.
  const veil = 1 - ((imageOpacity - 0.2) / 0.8) * 0.65;

  if (!slide) {
    return (
      <section
        className="relative mt-[76px] sm:mt-[92px] h-[calc(100svh-76px)] sm:h-[calc(100svh-92px)] min-h-[520px] sm:min-h-[560px] overflow-hidden grain bg-charcoal"
        data-testid="banner-slider-loading"
        aria-busy="true"
      />
    );
  }

  return (
    <section
      className="relative mt-[76px] sm:mt-[92px] h-[calc(100svh-76px)] sm:h-[calc(100svh-92px)] min-h-[520px] sm:min-h-[560px] overflow-hidden grain bg-charcoal"
      data-testid="banner-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1.12 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1 }, scale: { duration: 6, ease: "linear" } }}
          className="absolute inset-0"
        >
          <picture className="block w-full h-full" style={{ opacity: imageOpacity }} data-testid="banner-image-layer">
            {slide.imageVariants?.mobile?.url && <source media="(max-width: 767px)" srcSet={slide.imageVariants.mobile.url} />}
            <img
              src={slide.imageVariants?.desktop?.url || slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/45 to-charcoal/30" style={{ opacity: veil }} />
      <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-charcoal/70 to-transparent" style={{ opacity: veil }} />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.6)" }}
          >
            {slide.location && (
              <div className="flex items-center gap-3 text-[#D7B45A] font-display text-2xl sm:text-3xl mb-5 [text-shadow:0_2px_18px_rgba(215,180,90,0.45)]" data-testid="banner-location">
                <MapPin className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" strokeWidth={1.8} />
                <span>{slide.location}</span>
              </div>
            )}
            <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-3xl">
              {slide.title}
            </h1>
            {slide.type && <p className="text-white/85 text-lg mt-4">{slide.type}</p>}
            {slide.price && (
              <div
                data-testid="banner-price"
                className="mt-5 inline-flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2.5 rounded-2xl sm:rounded-full bg-charcoal/60 backdrop-blur-md border border-white/15 px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              >
                <span className="text-white/75 text-[10px] sm:text-xs uppercase tracking-[0.16em]">{t.projects.price}</span>
                <span className="font-display text-xl sm:text-2xl font-semibold text-white">{slide.price}</span>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex flex-wrap items-center gap-4 mt-9">
          <Link
            to={slide.slug ? `/projects/${slide.slug}` : "/projects"}
            data-testid="banner-view-btn"
            className="btn-shine group inline-flex items-center gap-2 rounded-full bg-gold text-charcoal font-semibold px-7 py-3.5 hover:bg-gold-soft transition-colors"
          >
            {slide.slug ? t.cta.viewProject : t.cta.explore}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
          </Link>
          <Link
            to="/contact"
            data-testid="banner-book-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-medium px-7 py-3.5 hover:bg-white/10 hover:border-white/50 transition-colors"
          >
            {t.cta.book}
          </Link>
        </div>
      </div>

      {/* Arrows */}
      {count > 1 && (
        <>
          <button
            onClick={() => go(idx - 1)}
            data-testid="banner-prev"
            className="absolute bottom-6 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 start-4 sm:start-8 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full glass border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            aria-label={t.common.previous}
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => go(idx + 1)}
            data-testid="banner-next"
            className="absolute bottom-6 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 end-4 sm:end-8 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full glass border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            aria-label={t.common.next}
          >
            {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>

          {/* Dots */}
          <div className="absolute bottom-7 inset-x-0 z-20 flex justify-center gap-2.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`banner-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-400 ${i === idx ? "w-8 bg-gold" : "w-1.5 bg-white/50 hover:bg-white"}`}
                aria-label={`${t.common.goToSlide} ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
